import type { TripStatus } from "@tc/shared";

/**
 * Which trip-detail view a user sees, as a pure function of audience + trip status (+ the back
 * office's explicit "edit the proposal" toggle).
 *
 * This exists as a *regression guard*. The club Accepted/Completed view once went silently missing
 * because the routing was a chain of booleans with an implicit `else` that dropped unhandled states
 * to the generic enquiry cards — a gap nothing could catch. Here every (audience, status) cell is
 * explicit and the `switch` is exhaustive: add a `TripStatus` and the compiler forces a decision,
 * and `trip-view.test.ts` pins every cell so a wrong/missing mapping fails the build.
 */
export type TripView =
  | "club-enquiry" // single scrolling page: trip details + the TC conversation
  | "club-proposal" // the proposal review/accept one-pager (ProposalSent)
  | "club-agreed" // tabbed agreed-trip view; Overview = the accepted-proposal summary
  | "club-generic" // tabbed generic view (terminal/limbo states with no bespoke design yet)
  | "bo-builder" // back-office STATE 1: the one-page proposal builder
  | "bo-tabbed"; // back-office STATE 2: the read-only tabbed trip view

export interface TripViewInput {
  isBackOffice: boolean;
  status: TripStatus;
  /** Back office only: the operator clicked Revise to amend an already-sent proposal. */
  boEditMode: boolean;
  /**
   * The club amended the request after the proposal went out (`isAmendedTrip`). Back office lands
   * straight in the builder so the amendment gets answered (UAT round 3 follow-up).
   */
  amended?: boolean;
  /** Back office only: the operator explicitly chose the tabbed view despite a pending amendment. */
  boTabbedOverride?: boolean;
}

function assertNever(x: never): never {
  throw new Error(`Unhandled trip status in resolveTripView: ${String(x)}`);
}

export function resolveTripView({
  isBackOffice,
  status,
  boEditMode,
  amended,
  boTabbedOverride,
}: TripViewInput): TripView {
  if (isBackOffice) {
    // STATE 1 (the builder) while building the first proposal (trip still Enquiry), when the
    // operator has explicitly clicked Revise, or when a club amendment is pending (so opening the
    // trip lands on the builder to answer it — unless the operator opted back to the tabbed view);
    // every other state is the read-only tabbed STATE 2.
    if (status === "Enquiry" || boEditMode) return "bo-builder";
    if (amended && !boTabbedOverride) return "bo-builder";
    return "bo-tabbed";
  }
  switch (status) {
    case "Enquiry":
      return "club-enquiry";
    case "ProposalSent":
      return "club-proposal";
    case "Accepted":
    case "Completed":
      return "club-agreed";
    case "Expired":
      // Same review one-pager as ProposalSent, rendered read-only (no accept) with a Resubmit CTA
      // into the pre-populated request wizard (UAT round 3 follow-up).
      return "club-proposal";
    case "Cancelled":
    case "Postponed":
      return "club-generic";
    default:
      // Exhaustiveness: a newly-added TripStatus makes this a compile error until it's handled.
      return assertNever(status);
  }
}
